import { type FC } from "react";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { Box, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useUser } from "@packsize/authentication";

import {
	populateProductCheckValue,
	useProductList,
} from "@/views/configuration/server-settings/modals/add-api-connection/product-list";
import { convertApiKeyScopesToList } from "@/views/configuration/server-settings/api-settings-tab";

import { ScopeCheckboxList } from "../../components/scope-checkbox-list";

interface ApiKeyInfoViewProps {
	readonly name: string;
	readonly tenantId: string;
	readonly scope?: string;
	readonly onCopyToClipboard: (value: string) => void;
}

export const ApiKeyInfoView: FC<ApiKeyInfoViewProps> = ({ name, tenantId, scope, onCopyToClipboard }) => {
	const intl = useIntl();
	const { hasLicense } = useUser();
	const productList = useProductList(hasLicense);
	const filteredProductsList = productList.filter((product) => product.hasLicense());

	return (
		<Stack spacing={3}>
			<Typography>
				<FormattedMessage
					id="Use the following information to connect to the API."
					defaultMessage="Use the following information to connect to the API."
				/>
			</Typography>

			<TextField
				label={intl.formatMessage({ id: "Name", defaultMessage: "Name" })}
				value={name}
				slotProps={{
					input: {
						readOnly: true,
					},
				}}
				fullWidth
			/>

			<TextField
				label={intl.formatMessage({ id: "Tenant ID", defaultMessage: "Tenant ID" })}
				value={tenantId}
				slotProps={{
					input: {
						readOnly: true,
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									size="small"
									onClick={() => onCopyToClipboard(tenantId)}
									data-pendoid="connection-info__button__copy-tenant-id"
								>
									<ContentCopyOutlinedIcon sx={{ fontSize: 20 }} />
								</IconButton>
							</InputAdornment>
						),
					},
				}}
				fullWidth
			/>

			<Box>
				<Typography>
					<FormattedMessage id="Services" defaultMessage="Services" />
				</Typography>
				<ScopeCheckboxList
					scopes={populateProductCheckValue(filteredProductsList, convertApiKeyScopesToList(scope))}
					readonly
				/>
			</Box>
		</Stack>
	);
};
